import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import get from "lodash/get"
import env from '~/libs/env'

import { localStorageService } from '~/libs/local-storage.service'

import { useBorrowerStore } from '~/store/borrower'
import { useQuestionsStore } from '~/store/questions'
import { useUserStore } from '~/store/user'
import { useTenantStore } from '~/store/tenant'
import { useRootStore } from '~/store/root'

export const useErcStore = defineStore('erc', () => {
  const nuxtApp = useNuxtApp()
  const { $axios, $lendioCookies } = nuxtApp

  const borrowerStore = useBorrowerStore()
  const questionsStore = useQuestionsStore()
  const userStore = useUserStore()
  const rootStore = useRootStore()
  const tenantStore = useTenantStore()

/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const checklistProgress = ref([])
  const finchConnected = ref(false)
  const loadingFinch = ref(false)
  const test = ref(false)
  const applications = ref([])
  const affiliatedBorrowers = ref([])
  const ercChecklistAgreementLegalText = ref(null)
  const ercChecklistFeeAgreementLegalText = ref(null)
  const ercChecklistAgreeERCPausedLegalText = ref(null)
  const ownershipInfoSkipped = ref(false)

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/
  const isTest = computed(() => {
    return test
  })
  // If tenant don't check for erc paused legal text
  const acknowledgementsAvailable = computed(() => {
    if (!tenantStore.isTenant) {
      if(
        ercChecklistAgreementLegalText.value &&
        ercChecklistFeeAgreementLegalText.value &&
        ercChecklistAgreeERCPausedLegalText.value
    ) {
      return true
    }
    return false
    } else {
      if(
        ercChecklistAgreementLegalText.value &&
        ercChecklistFeeAgreementLegalText.value
    ) {
      return true
    }
    return false
    }
  })
  const ercApplicationsStatus = computed(() => {
    const statusObj = {}
    return affiliatedBorrowers.value.reduce((status, affiliate, index) => {
      if (!statusObj[affiliate.ercApplicationStatus]) {
        statusObj[affiliate.ercApplicationStatus] = 1
      } else {
        statusObj[affiliate.ercApplicationStatus] = statusObj[affiliate.ercApplicationStatus]++
      }
      const sentToProcessor = Object.keys(statusObj).includes('sentToProcessor');
      if ((index === affiliatedBorrowers.value.length - 1) && !sentToProcessor) {
        if (Object.keys(statusObj).length === 1) {
          return Object.keys(statusObj)[0]
        }
      } else if (sentToProcessor) {
        return 'sentToProcessor'
      }
    }, 'multi-status')
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  async function createChecklistProgress() {
    const borrower = get(borrowerStore, 'borrower.id')
    let body = JSON.stringify({
      borrowerId: borrower
    })
    await $axios.post(`${env('apiUrl')}/erc-checklist-progress/new`, body, { headers: { 'Content-Type': 'application/json' } })
      .then(res => {
        let checklist = !res.data.data ? [] : res.data.data
        checklistProgress.value = checklist
      })
      .catch(err => {
        log.error('Failed to create checklist progress record', err)
      })
  }

  function setFinchConnected(value) {
    finchConnected.value = value
  }

  function setOwnershipInfoSkipped(value) {
    ownershipInfoSkipped.value = value
  }

  async function getChecklistProgress() {
    const borrower = get(borrowerStore, 'borrower.id')
    await $axios.get(`${env('apiUrl')}/erc-checklist-progress/${borrower}`)
      .then(res => {
        let checklist = !res.data.data ? [] : res.data.data
        checklistProgress.value = checklist
      })
      .catch(err => {
        log.error('Error fetching checklist progress', err)
      })
  }
  async function getAgreeERCPausedLegalText() {
    await $axios.get(`${env('apiUrl')}/legal-text/erc_agree_paused`)
      .then(res => {
        const ercAgreePaused = get(res, 'data.data.value')
        ercChecklistAgreeERCPausedLegalText.value = !ercAgreePaused ? '' : ercAgreePaused
      })
      .catch(err => {
        log.error('Error fetching checklist agreement legal text', err)
      })
  }

  async function getChecklistFeeAgreementLegalText() {
    await $axios.get(`${env('apiUrl')}/legal-text/erc_check_fee_agree`)
      .then(res => {
        let text = get(res, 'data.data.value')
        ercChecklistFeeAgreementLegalText.value = !text ? '' : text
      }).then()
      .catch(err => {
        log.error('Error fetching checklist fee agreement legal text', err)
      })
  }

  async function getChecklistAgreementLegalText() {
    await $axios.get(`${env('apiUrl')}/legal-text/erc_check_agree`)
      .then(res => {
        let text = get(res, 'data.data.value')
        ercChecklistAgreementLegalText.value = !text ? '' : text
      })
      .catch(err => {
        log.error('Error fetching checklist agreement legal text', err)
      })
  }

  async function addFinchConnectedBorrowerValue() {
    const borrowerId = get(borrowerStore, 'borrower.id')
    finchConnected.value = true
    questionsStore.updateAnswer({ alias: 'finchConnected', value: true })
    questionsStore.saveAnswers({ answers: null, initialAuth: false, borrowerId })
    this.updateChecklistProgress('payrollDocs')
  }

  async function updateFinchConnectedThroughQuestions() {
    finchConnected.value = true
    questionsStore.updateAnswer({ alias: 'finchConnected', value: true }, { root: true })
    questionsStore.saveAnswers({ answers: null, initialAuth: false }, { root: true })

    this.updateChecklistProgress('payrollDocs')
  }

  async function updateChecklistProgress(page) {
    const borrower = get(borrowerStore, 'borrower.id')
    let checklist = checklistProgress.value.id
    let body = JSON.stringify({
      borrowerId: borrower,
      page: page
    })

    if (!checklist) {
      await this.createChecklistProgress()
      checklist = checklistProgress.value.id
    }
    await $axios.put(`${env('apiUrl')}/erc-checklist-progress/${checklist}`, body, {headers: {'Content-Type': 'application/json'}})
      .then(res => {
        let checklist = !res.data.data ? [] : res.data.data
        checklistProgress.value = checklist
      })
      .catch(err => {
        log.error('Failed to update checklist progress', err)
      })
  }

  function setTest(payload) {
    test.value = payload === (undefined || null) ? localStorageService.getItem('ercTest') : payload == 'true'
    localStorageService.setItem('ercTest', test.value)
  }

  async function fetchAffiliatedBorrowers() {
    const user = get(rootStore, 'userProfile');
    await $axios.get(`${env('apiUrl')}/user/${user.id}/erc-affiliates`, {headers: {'Content-Type': 'application/json'}})
      .then(res => {
          let _affiliatedBorrowers = !res.data.data ? [] : res.data.data
          affiliatedBorrowers.value = _affiliatedBorrowers
        })
      .catch(err => {
        log.error('failed to get affiliated borrowers', err)
      })
  }

  async function redirectToErcFinal(route, redirect) {
    const borrowerId = get(borrowerStore, 'borrowerId');

    await userStore.getBorrowers();
    const borrowers = get(userStore, 'borrowers')
    const path = '/erc/final-erc-affiliate'

    if (borrowers && borrowers.length > 1) {
      await $axios.get(`${env('apiUrl')}/borrower/${borrowerId}/erc-deal-submitted`, {headers: {'Content-Type': 'application/json'}})
      .then(res => {
          if (res.data.data) {
            if (route.path !== path) {
              return redirect({path});
            }
          }
        });
    }
  }

  async function navigateToCorrectRouteForErcBorrower() {
    const borrowers = get(userStore, 'borrowers')
    const path = '/erc/final-erc-affiliate'
    if (borrowers && borrowers.length > 1) {
      await navigateTo({path})
    }
  }

  function resumeApplicationForSpecifiedBorrower(borrowerId, appStatus) {
    $lendioCookies.set('borrowerId', borrowerId, {path: '/bp/'})
    if (appStatus === 'complete' || appStatus === 'sentToProcessor') {
      location = '/bp/erc/checklist'
    } else {
      location = '/bp/resume-app?type=erc'
    }
  }

  async function postUserErcApplications(affiliatedBorrowers) {
    const newDate = new Date()
    const user = get(rootStore, 'userProfile')
    const borrowerIds = this.formatAffiliatedBorrowerIds(affiliatedBorrowers)
    const ercApplicationComplete = newDate.toISOString();
    const body = {
      borrowerIds,
      ercApplicationComplete
    }

    await $axios.post(`${env('apiUrl')}/user-erc-application/user/${user.id}`, body, {headers: {'Content-Type': 'application/json'}})
      .then(res => {
        let _applications = !res.data.data ? [] : res.data.data
        applications.value = _applications
        this.fetchAffiliatedBorrowers();
      })
      .catch(err => {
        log.error('failed to post userErcApplication ', err)
      })
  }

  function formatAffiliatedBorrowerIds(affiliatedBorrowers) {
    return affiliatedBorrowers.map(affiliatedBorrower => {
      return affiliatedBorrower.borrowerId
    })
  }

  return {
    // STATE
    checklistProgress,
    finchConnected,
    loadingFinch,
    test,
    affiliatedBorrowers,
    ercChecklistAgreementLegalText,
    ercChecklistFeeAgreementLegalText,
    ercChecklistAgreeERCPausedLegalText,
    ownershipInfoSkipped,

    // GETTERS
    isTest,
    acknowledgementsAvailable,
    ercApplicationsStatus,

    // ACTIONS
    createChecklistProgress,
    setFinchConnected,
    setOwnershipInfoSkipped,
    getChecklistProgress,
    getAgreeERCPausedLegalText,
    getChecklistFeeAgreementLegalText,
    getChecklistAgreementLegalText,
    addFinchConnectedBorrowerValue,
    updateFinchConnectedThroughQuestions,
    updateChecklistProgress,
    setTest,
    fetchAffiliatedBorrowers,
    redirectToErcFinal,
    navigateToCorrectRouteForErcBorrower,
    resumeApplicationForSpecifiedBorrower,
    postUserErcApplications,
    formatAffiliatedBorrowerIds,
  }
})
